import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  message,
  Switch,
  Space,
  Modal,
  Popconfirm,
  Tag,
  Tooltip,
  Collapse,
  Table,
  Spin,
  Empty,
  Upload,
  Avatar,
  Slider,
  Divider,
  Typography,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  SettingOutlined,
  UploadOutlined,
  HolderOutlined,
  StarFilled,
  StarOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getModelListUnifiedV2, getUnifiedV2MinVersion, getCatalogueStandards } from '../../graphql/queries';
import {
  updateCreditConfigUnifiedV2,
  getSignedUrl,
  getObjectUrl,
  updateUnifiedV2MinVersion,
} from '../../graphql/mutation';
import { uploadToR2 } from '../../utils/uploadToR2';

const { Title } = Typography;

const SortableModelWrapper = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        display: 'flex',
        alignItems: 'stretch',
        marginBottom: 16,
        border: '1px solid #d9d9d9',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <div
        ref={setActivatorNodeRef}
        {...listeners}
        {...attributes}
        title="Drag to reorder"
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px',
          cursor: isDragging ? 'grabbing' : 'grab',
          color: '#bfbfbf',
          background: '#fafafa',
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        <HolderOutlined />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
};

const EMPTY_MODEL_DIALOG = {
  visible: false,
  modelId: '',
  modelName: '',
  description: '',
  badge: '',
  icon: '',
  iconFile: null,
  costPreview: 1,
  maxCharacterLimit: null,
  hasAudioFeature: false,
  isAudioOptional: true,
  audioOnValue: '',
  audioOffValue: '',
  isDefault: false,
  isFreeCropImage: false,
  resolutionRows: [], // [{ key, value, name, priceOn, priceOff }]
};

// Standard-driven chips: clicking an inactive chip adds a row prefilled from that
// standard (key/name/short_name/...); clicking an already-added (highlighted) chip
// removes it. Only keys not already present on this list are offered.
const StandardChips = ({ standards, existingKeys, onAdd, onRemove, color = '#1677ff' }) => (
  <Space wrap size={8} style={{ marginBottom: 8 }}>
    {(standards || []).map((s) => {
      const isActive = existingKeys.has(s.key);
      return (
        <Tooltip key={s.key} title={s.label}>
          <Button
            size="small"
            onClick={() => (isActive ? onRemove(s.key) : onAdd(s))}
            style={isActive ? { borderColor: color, color, background: `${color}14` } : {}}
          >
            {s.key}
          </Button>
        </Tooltip>
      );
    })}
  </Space>
);

// Fallback for legacy rows saved before the standards catalog existed — lets an
// admin backfill a `key` on a row that doesn't have one yet, so the next save
// doesn't get rejected with INVALID_STANDARD_KEY.
const LegacyKeyPicker = ({ standards, onPick }) => (
  <Space size={4}>
    <Tag icon={<WarningOutlined />} color="warning">Not linked</Tag>
    <Select
      size="small"
      placeholder="Pick a key"
      style={{ width: 140 }}
      options={(standards || []).map((s) => ({ value: s.key, label: s.label }))}
      onChange={onPick}
    />
  </Space>
);

// Free-form ratio list editor (used for cropRatios / textToVideoRatios / imgToVideoRatios).
// Rows are keyed off the shared ratio standards catalog — `key`/`name`/`short_name`
// (and, for crop ratios, aspect_ratio_x/y) come from the standard and are read-only;
// only `value` (the provider-specific raw parameter) is editable per model.
const RatioListEditor = ({ title, rows, onChange, defaultValue, onSetDefault, withAspectXY = false, standardsList }) => {
  const existingKeys = new Set((rows || []).map((r) => r.key).filter(Boolean));

  const rowFromStandard = (s, previousValue) =>
    withAspectXY
      ? { key: s.key, value: previousValue ?? s.key, name: s.label, short_name: s.short_name, aspect_ratio_x: s.aspect_ratio_x ?? null, aspect_ratio_y: s.aspect_ratio_y ?? null }
      : { key: s.key, value: previousValue ?? s.key, name: s.label, short_name: s.short_name };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1, marginBottom: 8 }}>
        {title}
      </div>
      <StandardChips
        standards={standardsList}
        existingKeys={existingKeys}
        onAdd={(s) => onChange([...(rows || []), rowFromStandard(s)])}
        onRemove={(key) => onChange((rows || []).filter((r) => r.key !== key))}
      />
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        {(rows || []).map((row, i) => (
          <Space key={i} style={{ width: '100%' }}>
            <Tooltip title={defaultValue != null && row.key === defaultValue ? 'Default' : 'Set as default'}>
              <Button
                type="text"
                size="small"
                icon={defaultValue != null && row.key === defaultValue ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                onClick={() => onSetDefault && onSetDefault(row.key)}
              />
            </Tooltip>
            {row.key ? (
              <Tag style={{ minWidth: 60, textAlign: 'center' }}>{row.key}</Tag>
            ) : (
              <LegacyKeyPicker
                standards={(standardsList || []).filter((s) => !existingKeys.has(s.key))}
                onPick={(key) => {
                  const s = (standardsList || []).find((x) => x.key === key);
                  if (!s) return;
                  const next = JSON.parse(JSON.stringify(rows));
                  next[i] = rowFromStandard(s, row.value);
                  onChange(next);
                }}
              />
            )}
            <Input
              size="small"
              placeholder="value"
              title="value (provider-specific raw parameter)"
              style={{ width: 90 }}
              value={row.value}
              onChange={(e) => {
                const next = JSON.parse(JSON.stringify(rows));
                next[i].value = e.target.value;
                onChange(next);
              }}
            />
            {withAspectXY ? (
              <span style={{ width: 60, fontSize: 12, color: '#999' }}>
                {row.aspect_ratio_x != null && row.aspect_ratio_y != null ? `${row.aspect_ratio_x}x${row.aspect_ratio_y}` : '—'}
              </span>
            ) : (
              <>
                <span style={{ width: 140, fontSize: 12, color: '#666' }}>{row.name}</span>
                <span style={{ width: 80, fontSize: 12, color: '#999' }}>{row.short_name}</span>
              </>
            )}
          </Space>
        ))}
      </Space>
    </div>
  );
};

export const UnifiedCreditConfigV2 = () => {
  const [unifiedConfig, setUnifiedConfig] = useState({ models: [] });
  const [addModelDialog, setAddModelDialog] = useState(EMPTY_MODEL_DIALOG);
  const [editModelDialog, setEditModelDialog] = useState({ ...EMPTY_MODEL_DIALOG, modelIndex: -1 });
  const [minVersionModalVisible, setMinVersionModalVisible] = useState(false);
  const [minVersion, setMinVersion] = useState('');
  const [minVersionInput, setMinVersionInput] = useState('');

  const { data: minVersionData } = useQuery(getUnifiedV2MinVersion);
  const [updateMinVersionMutation] = useMutation(updateUnifiedV2MinVersion);

  // Shared duration/resolution/ratio key catalog (managed from the "Standards" tab).
  const { data: standardsData } = useQuery(getCatalogueStandards);
  const durationStandards = standardsData?.getCatalogueStandards?.durations || [];
  const resolutionStandards = standardsData?.getCatalogueStandards?.resolutions || [];
  const ratioStandards = standardsData?.getCatalogueStandards?.ratios || [];

  useEffect(() => {
    if (minVersionData?.getUnifiedV2MinVersion !== undefined) {
      const v = minVersionData.getUnifiedV2MinVersion ?? '';
      setMinVersion(v);
      setMinVersionInput(v);
    }
  }, [minVersionData]);

  const handleSaveMinVersion = async () => {
    try {
      await updateMinVersionMutation({ variables: { version: minVersionInput } });
      setMinVersion(minVersionInput);
      setMinVersionModalVisible(false);
      message.success('Minimum unified V2 config version updated.');
    } catch (err) {
      message.error('Failed to update: ' + err.message);
    }
  };

  const modelOrderRef = useRef(
    (() => {
      try { return JSON.parse(localStorage.getItem('unifiedV2ModelOrder')) || null; } catch { return null; }
    })()
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const { data, loading, refetch } = useQuery(getModelListUnifiedV2);
  const [updateMutation] = useMutation(updateCreditConfigUnifiedV2);
  const [getSignedUrlMutation] = useMutation(getSignedUrl);
  const [getObjectUrlMutation] = useMutation(getObjectUrl);

  const saveOrder = (ids) => {
    modelOrderRef.current = ids;
    try { localStorage.setItem('unifiedV2ModelOrder', JSON.stringify(ids)); } catch {}
  };

  const applyOrder = (models) => {
    const order = modelOrderRef.current;
    if (!order || !order.length) return models;
    return [...models].sort((a, b) => {
      const ai = order.indexOf(a.id);
      const bi = order.indexOf(b.id);
      return (ai === -1 ? 9999 : ai) - (bi === -1 ? 9999 : bi);
    });
  };

  // Older saved data carried a stray "is_default" flag on individual ratio/resolution/duration
  // rows (from a previous UI iteration), and used "label" where the field is now "name".
  // Normalize both away here so they stop being resaved. short_name backfills from name/value
  // for rows saved before that field existed. `key` is preserved as-is (undefined for legacy
  // rows saved before the standards catalog existed).
  const stripRowDefaultFlag = (rows) =>
    (rows || []).map(({ key, value, name, label, short_name }) => {
      const rowName = name ?? label ?? '';
      return { key, value, name: rowName, short_name: short_name ?? rowName ?? value ?? '' };
    });

  // Crop ratios also carry aspect_ratio_x/y (used by mobile for cropping). Backfill them
  // from the value string for older rows saved before this field existed.
  const parseRatioXY = (value) => {
    const m = /^(\d+)\s*:\s*(\d+)$/.exec(String(value || '').trim());
    return m ? { x: Number(m[1]), y: Number(m[2]) } : { x: null, y: null };
  };

  const normalizeCropRatios = (rows) =>
    (rows || []).map(({ key, value, name, label, short_name, aspect_ratio_x, aspect_ratio_y }) => {
      const rowName = name ?? label ?? '';
      const rowShortName = short_name ?? rowName ?? value ?? '';
      if (aspect_ratio_x != null && aspect_ratio_y != null) {
        return { key, value, name: rowName, short_name: rowShortName, aspect_ratio_x, aspect_ratio_y };
      }
      const { x, y } = parseRatioXY(value);
      return { key, value, name: rowName, short_name: rowShortName, aspect_ratio_x: x, aspect_ratio_y: y };
    });

  // Also migrates resolution.label / duration.label (older field name) to .name.
  const stripResolutionDefaultFlags = (resolutions) =>
    (resolutions || []).map(({ is_default: _resIsDefault, resolution, durations, ...rest }) => ({
      ...rest,
      resolution: resolution
        ? { key: resolution.key, value: resolution.value, name: resolution.name ?? resolution.label ?? '' }
        : resolution,
      durations: (durations || []).map(({ is_default: _durIsDefault, duration, ...durRest }) => ({
        ...durRest,
        duration: duration
          ? {
              key: duration.key,
              value: duration.value,
              seconds: duration.seconds,
              name: duration.name ?? duration.label ?? (duration.seconds != null ? `${duration.seconds} seconds` : ''),
              short_name: duration.short_name ?? duration.label ?? (duration.seconds != null ? `${duration.seconds}s` : ''),
            }
          : duration,
      })),
    }));

  // Before the ratio default was split per list (crop/text-to-video/image-to-video), it lived
  // under one generic "aspectRatio" key. Drop it here so it stops being carried forward —
  // only the three split keys are read by anything now.
  const normalizeDefaultValues = ({ aspectRatio: _staleAspectRatio, ...rest } = {}) => rest;

  // Default selections used to be stored as the row's `value` (provider-specific raw
  // parameter); they're now stored/compared as `key` (stable standard identity). Migrate
  // any old value-based default found in saved data by matching it against the row's
  // `value` and swapping in that row's `key` — so it resaves in the new format instead of
  // silently going stale.
  const migrateDefaultToKey = (rows, oldVal) => {
    if (oldVal == null) return oldVal;
    const match = (rows || []).find((r) => r.value === oldVal);
    return match ? match.key : oldVal;
  };

  useEffect(() => {
    if (data?.getModelListUnifiedV2) {
      const raw = JSON.parse(JSON.stringify(data.getModelListUnifiedV2));
      const models = (raw.models || []).map((m) => {
        const cropRatios = normalizeCropRatios(m.cropRatios);
        const textToVideoRatios = stripRowDefaultFlag(m.textToVideoRatios);
        const imgToVideoRatios = stripRowDefaultFlag(m.imgToVideoRatios);
        const resolutions = stripResolutionDefaultFlags(m.resolutions);
        const resolutionRowsForMigration = (resolutions || []).map((r) => ({ key: r.resolution?.key, value: r.resolution?.value }));
        const durationRowsForMigration = (resolutions || []).flatMap((r) =>
          (r.durations || []).map((d) => ({ key: d.duration?.key, value: d.duration?.value }))
        );
        const dv = normalizeDefaultValues(m.default_values);

        return {
          ...m,
          has_audio_feature: m.has_audio_feature ?? false,
          is_audio_optional: m.is_audio_optional ?? true,
          is_default: m.is_default ?? false,
          is_free_crop_image: m.is_free_crop_image ?? false,
          cropRatios,
          textToVideoRatios,
          imgToVideoRatios,
          resolutions,
          default_values: {
            ...dv,
            cropAspectRatio: migrateDefaultToKey(cropRatios, dv.cropAspectRatio),
            textToVideoAspectRatio: migrateDefaultToKey(textToVideoRatios, dv.textToVideoAspectRatio),
            imgToVideoAspectRatio: migrateDefaultToKey(imgToVideoRatios, dv.imgToVideoAspectRatio),
            resolution: migrateDefaultToKey(resolutionRowsForMigration, dv.resolution),
            duration: migrateDefaultToKey(durationRowsForMigration, dv.duration),
          },
        };
      });
      setUnifiedConfig({ models: applyOrder(models) });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleModelDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setUnifiedConfig((prev) => {
      const oldIdx = prev.models.findIndex((m) => m.id === active.id);
      const newIdx = prev.models.findIndex((m) => m.id === over.id);
      if (oldIdx === -1 || newIdx === -1) return prev;
      const reordered = arrayMove(prev.models, oldIdx, newIdx);
      saveOrder(reordered.map((m) => m.id));
      return { models: reordered };
    });
  };

  // ---- Icon upload helper ----

  const uploadIcon = async (iconFile) => {
    const { data: signedUrlData } = await getSignedUrlMutation({
      variables: { fileName: iconFile.name },
    });
    const uploadResult = await uploadToR2(
      iconFile,
      signedUrlData.getSignedUrl.url,
      signedUrlData.getSignedUrl.contentType
    );
    if (!uploadResult.success) throw new Error(uploadResult.message);
    const { data: objectUrlData } = await getObjectUrlMutation({
      variables: { key: signedUrlData.getSignedUrl.id, eTag: 'etag' },
    });
    return objectUrlData.getObjectUrl;
  };

  // ---- CRUD: Models ----

  const countDefaultModels = () =>
    (unifiedConfig.models || []).filter((m) => m.is_default === true).length;

  const handleAddModel = async () => {
    const d = addModelDialog;
    if (!d.modelId || !d.modelName) {
      message.error('Please fill in Model ID and Model Name');
      return;
    }
    if (d.isDefault && countDefaultModels() >= 2) {
      message.error('Cannot mark more than 2 models as default. Please unmark another default model first.');
      return;
    }
    try {
      let iconUrl = d.icon;
      if (d.iconFile) iconUrl = await uploadIcon(d.iconFile);

      const newModel = {
        id: d.modelId,
        name: d.modelName,
        description: d.description || '',
        badge: d.badge || '',
        icon: iconUrl || '',
        cost_preview: d.costPreview || 1,
        maxCharacterLimit: d.maxCharacterLimit || undefined,
        has_audio_feature: d.hasAudioFeature,
        is_audio_optional: d.isAudioOptional,
        audioOnValue: d.hasAudioFeature ? (d.audioOnValue || undefined) : undefined,
        audioOffValue: d.hasAudioFeature ? (d.audioOffValue || undefined) : undefined,
        is_default: d.isDefault,
        is_free_crop_image: d.isFreeCropImage,
        cropRatios: [],
        textToVideoRatios: [],
        imgToVideoRatios: [],
        default_values: {},
        resolutions: buildResolutionsFromRows(d.resolutionRows, d.hasAudioFeature, d.isAudioOptional, []),
      };

      setUnifiedConfig((prev) => ({ models: [...prev.models, newModel] }));
      setAddModelDialog(EMPTY_MODEL_DIALOG);
      message.success('Model added');
    } catch (err) {
      message.error('Failed to add model: ' + err.message);
    }
  };

  const handleEditModel = async () => {
    const d = editModelDialog;
    if (!d.modelId || !d.modelName) {
      message.error('Please fill in Model ID and Model Name');
      return;
    }
    if (d.isDefault) {
      const currentModel = unifiedConfig.models[d.modelIndex];
      if (!currentModel?.is_default && countDefaultModels() >= 2) {
        message.error('Cannot mark more than 2 models as default. Please unmark another default model first.');
        return;
      }
    }
    try {
      let iconUrl = d.icon;
      if (d.iconFile) iconUrl = await uploadIcon(d.iconFile);

      setUnifiedConfig((prev) => {
        const newModels = JSON.parse(JSON.stringify(prev.models));
        const model = newModels[d.modelIndex];
        newModels[d.modelIndex] = {
          ...model,
          id: d.modelId,
          name: d.modelName,
          description: d.description || '',
          badge: d.badge || '',
          icon: iconUrl || '',
          cost_preview: d.costPreview || 1,
          maxCharacterLimit: d.maxCharacterLimit || undefined,
          has_audio_feature: d.hasAudioFeature,
          is_audio_optional: d.isAudioOptional,
          audioOnValue: d.hasAudioFeature ? (d.audioOnValue || undefined) : undefined,
          audioOffValue: d.hasAudioFeature ? (d.audioOffValue || undefined) : undefined,
          is_default: d.isDefault,
          is_free_crop_image: d.isFreeCropImage,
          resolutions: buildResolutionsFromRows(
            d.resolutionRows,
            d.hasAudioFeature,
            d.isAudioOptional,
            model.resolutions || [],
          ),
        };
        return { models: newModels };
      });

      setEditModelDialog({ ...EMPTY_MODEL_DIALOG, modelIndex: -1 });
      message.success('Model updated');
    } catch (err) {
      message.error('Failed to update model: ' + err.message);
    }
  };

  const handleRemoveModel = (modelIndex) => {
    setUnifiedConfig((prev) => ({
      models: prev.models.filter((_, i) => i !== modelIndex),
    }));
  };

  // ---- CRUD: ratio lists (standards-backed) ----

  const setRatioList = (modelIndex, field, rows) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex][field] = rows;
      return { models: nm };
    });
  };

  // ---- CRUD: Resolutions ----
  // Resolutions (key/value/name/price) are managed from the Add/Edit Model form's
  // resolutionRows table. Existing durations are preserved by matching on
  // resolution.key; rows removed from the form drop their resolution (and
  // its durations) entirely.

  const buildResolutionsFromRows = (rows, hasAudio, isOptional, existingResolutions) => {
    return (rows || [])
      .filter((row) => row.key && row.value)
      .map((row) => {
        const existing = (existingResolutions || []).find((r) => r.resolution?.key === row.key);
        const pricing_per_second =
          hasAudio && isOptional
            ? { audio_on: row.priceOn ?? 0, audio_off: row.priceOff ?? 0 }
            : (row.priceOn ?? 0);
        return {
          resolution: { key: row.key, value: row.value, name: row.name },
          pricing_per_second,
          durations: existing?.durations || [],
        };
      });
  };

  const resolutionsToRows = (resolutions) =>
    (resolutions || []).map((r) => ({
      key: r.resolution?.key || '',
      value: r.resolution?.value || '',
      name: r.resolution?.name || '',
      priceOn: typeof r.pricing_per_second === 'object' ? r.pricing_per_second?.audio_on : r.pricing_per_second,
      priceOff: typeof r.pricing_per_second === 'object' ? r.pricing_per_second?.audio_off : null,
    }));

  const handleRemoveResolution = (modelIndex, resIndex) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex].resolutions.splice(resIndex, 1);
      return { models: nm };
    });
  };

  const assignLegacyResolutionKey = (modelIndex, resIndex, standard) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      const res = nm[modelIndex]?.resolutions?.[resIndex];
      if (!res) return prev;
      res.resolution = { key: standard.key, value: res.resolution?.value || standard.key, name: standard.label };
      return { models: nm };
    });
  };

  // Default selections live in a single model.default_values object
  // ({ isAudio, resolution, duration, aspectRatio }) rather than being spread
  // across is_default flags on individual resolution/duration/ratio rows.
  const setDefaultValue = (modelIndex, field, value) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex].default_values = { ...(nm[modelIndex].default_values || {}), [field]: value };
      return { models: nm };
    });
  };

  const setDefaultResolution = (modelIndex, resValue) => setDefaultValue(modelIndex, 'resolution', resValue);
  const setDefaultDuration = (modelIndex, durValue) => setDefaultValue(modelIndex, 'duration', durValue);
  // Crop / text-to-video / image-to-video ratios are independent lists, so each gets its own default field.
  const setDefaultCropRatio = (modelIndex, ratioValue) => setDefaultValue(modelIndex, 'cropAspectRatio', ratioValue);
  const setDefaultTextToVideoRatio = (modelIndex, ratioValue) => setDefaultValue(modelIndex, 'textToVideoAspectRatio', ratioValue);
  const setDefaultImgToVideoRatio = (modelIndex, ratioValue) => setDefaultValue(modelIndex, 'imgToVideoAspectRatio', ratioValue);

  // ---- CRUD: Durations (standards-backed) ----

  // Duration present in every resolution of the model is considered "globally active".
  const getGlobalDurationActiveSet = (model) => {
    const resolutions = model.resolutions || [];
    if (resolutions.length === 0) return new Set();
    const perResKeys = resolutions.map(
      (r) => new Set((r.durations || []).map((d) => d.duration?.key))
    );
    const active = new Set();
    (perResKeys[0] || new Set()).forEach((k) => {
      if (k && perResKeys.every((set) => set.has(k))) active.add(k);
    });
    return active;
  };

  // Toggling a duration at the model level adds/removes it across ALL resolutions at once.
  const toggleDurationGlobal = (modelIndex, standard, isActive) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      const model = nm[modelIndex];
      (model.resolutions || []).forEach((resObj) => {
        if (isActive) {
          resObj.durations = (resObj.durations || []).filter((d) => d.duration?.key !== standard.key);
        } else {
          const exists = (resObj.durations || []).some((d) => d.duration?.key === standard.key);
          if (!exists) {
            resObj.durations = [
              ...(resObj.durations || []),
              {
                duration: {
                  key: standard.key,
                  value: standard.key,
                  name: standard.label,
                  short_name: standard.short_name,
                  seconds: standard.seconds,
                },
                cost: 0,
              },
            ].sort((a, b) => (a.duration.seconds ?? 0) - (b.duration.seconds ?? 0));
          }
        }
      });
      return { models: nm };
    });
  };

  const updateDurationField = (modelIndex, resIndex, durIndex, field, val) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      const dur = nm[modelIndex]?.resolutions?.[resIndex]?.durations?.[durIndex];
      if (!dur) return prev;
      dur.duration[field] = val;
      return { models: nm };
    });
  };

  const assignLegacyDurationKey = (modelIndex, resIndex, durIndex, standard) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      const dur = nm[modelIndex]?.resolutions?.[resIndex]?.durations?.[durIndex];
      if (!dur) return prev;
      dur.duration = {
        key: standard.key,
        value: dur.duration?.value || standard.key,
        name: standard.label,
        short_name: standard.short_name,
        seconds: standard.seconds,
      };
      return { models: nm };
    });
  };

  const handleRemoveDuration = (modelIndex, resIndex, durIndex) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex].resolutions[resIndex].durations.splice(durIndex, 1);
      return { models: nm };
    });
  };

  // ---- Save ----

  const handleSave = async () => {
    try {
      saveOrder(unifiedConfig.models.map((m) => m.id));
      await updateMutation({ variables: { creditJson: unifiedConfig } });
      message.success('Saved. Recalculating costs...');
      const refreshed = await refetch();
      if (refreshed?.data?.getModelListUnifiedV2) {
        const raw = JSON.parse(JSON.stringify(refreshed.data.getModelListUnifiedV2));
        setUnifiedConfig({ models: applyOrder(raw.models || []) });
      }
      message.success('Unified V2 configuration saved & costs recalculated.');
    } catch (err) {
      message.error('Save failed: ' + err.message);
    }
  };

  // ---- Duration table columns ----

  const createDurationColumns = (modelIndex, resIndex, hasAudio, isAudioOptional, defaultDurationValue) => {
    const costCols =
      hasAudio && isAudioOptional
        ? [
            {
              title: 'Audio Off',
              key: 'cost_off',
              width: 130,
              render: (_, record) => (
                <InputNumber value={record?.cost?.without_audio ?? 0} disabled min={0} style={{ width: '100%' }} prefix="💎" />
              ),
            },
            {
              title: 'Audio On',
              key: 'cost_on',
              width: 130,
              render: (_, record) => (
                <InputNumber value={record?.cost?.with_audio ?? 0} disabled min={0} style={{ width: '100%' }} prefix="💎" />
              ),
            },
          ]
        : [
            {
              title: 'Cost (Credits)',
              key: 'cost',
              width: 150,
              render: (_, record) => (
                <InputNumber value={record?.cost ?? 0} disabled min={0} style={{ width: '100%' }} prefix="💎" />
              ),
            },
          ];

    return [
      {
        title: 'Default',
        key: 'is_default',
        width: 50,
        render: (_, record) => {
          const isDefaultDur = defaultDurationValue != null && record.duration?.key === defaultDurationValue;
          return (
            <Tooltip title={isDefaultDur ? 'Default' : 'Set as default'}>
              <Button
                type="text"
                size="small"
                icon={isDefaultDur ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                onClick={() => setDefaultDuration(modelIndex, record.duration?.key)}
              />
            </Tooltip>
          );
        },
      },
      {
        title: 'Key',
        key: 'key',
        width: 130,
        render: (_, record, index) => record.duration?.key ? <Tag>{record.duration.key}</Tag> : (
          <LegacyKeyPicker
            standards={durationStandards}
            onPick={(key) => {
              const s = durationStandards.find((x) => x.key === key);
              if (s) assignLegacyDurationKey(modelIndex, resIndex, index, s);
            }}
          />
        ),
      },
      {
        title: 'Name',
        key: 'name',
        width: 160,
        render: (_, record) => <span style={{ color: '#666' }}>{record.duration?.name}</span>,
      },
      {
        title: 'Short Name',
        key: 'short_name',
        width: 100,
        render: (_, record) => <span style={{ color: '#999' }}>{record.duration?.short_name}</span>,
      },
      ...costCols,
      {
        title: 'Value',
        key: 'value',
        width: 120,
        render: (_, record, index) => (
          <Input
            size="small"
            value={record.duration?.value}
            onChange={(e) => updateDurationField(modelIndex, resIndex, index, 'value', e.target.value)}
          />
        ),
      },
      {
        title: 'Seconds',
        dataIndex: ['duration', 'seconds'],
        key: 'seconds',
      },
      {
        title: 'Action',
        key: 'action',
        width: 100,
        render: (_, record, index) => (
          <Popconfirm
            title="Remove Duration"
            description={`Remove ${record.duration?.name}?`}
            onConfirm={() => handleRemoveDuration(modelIndex, resIndex, index)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger size="small" icon={<DeleteOutlined />}>Remove</Button>
          </Popconfirm>
        ),
      },
    ];
  };

  // ---- Shared model form (Add & Edit) ----

  const renderModelForm = (dialogState, setDialogState) => (
    <Form layout="vertical" style={{ marginTop: 16 }}>
      <Form.Item label="Model ID" required>
        <Input
          value={dialogState.modelId}
          onChange={(e) => setDialogState((prev) => ({ ...prev, modelId: e.target.value }))}
          placeholder="e.g., pixverse-v5"
        />
      </Form.Item>
      <Form.Item label="Model Name" required>
        <Input
          value={dialogState.modelName}
          onChange={(e) => setDialogState((prev) => ({ ...prev, modelName: e.target.value }))}
          placeholder="e.g., Pixverse 5"
        />
      </Form.Item>
      <Form.Item label="Badge">
        <Input
          value={dialogState.badge}
          onChange={(e) => setDialogState((prev) => ({ ...prev, badge: e.target.value }))}
          placeholder="e.g., New, Popular (optional)"
        />
      </Form.Item>
      <Form.Item label="Description">
        <Input.TextArea
          value={dialogState.description}
          rows={3}
          onChange={(e) => setDialogState((prev) => ({ ...prev, description: e.target.value }))}
          placeholder="Brief description of the model (optional)"
        />
      </Form.Item>
      <Form.Item label="Model Icon">
        {dialogState.icon && (
          <div style={{ marginBottom: 8 }}>
            <img
              src={dialogState.icon}
              alt="Current icon"
              style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
            />
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>Current icon</div>
          </div>
        )}
        <Upload
          accept="image/*,image/svg+xml"
          maxCount={1}
          beforeUpload={(file) => {
            const isImage = file.type.startsWith('image/') || file.type === 'image/svg+xml';
            if (!isImage) { message.error('Only image files allowed!'); return Upload.LIST_IGNORE; }
            if (file.size / 1024 / 1024 >= 5) { message.error('Max 5MB'); return Upload.LIST_IGNORE; }
            setDialogState((prev) => ({ ...prev, iconFile: file }));
            return false;
          }}
          onRemove={() => setDialogState((prev) => ({ ...prev, iconFile: null }))}
        >
          <Button icon={<UploadOutlined />}>
            {dialogState.icon ? 'Change Icon' : 'Upload Icon (Optional)'}
          </Button>
        </Upload>
      </Form.Item>
      <Form.Item label="Cost Preview">
        <Slider
          min={1}
          max={5}
          value={dialogState.costPreview || 1}
          onChange={(val) => setDialogState((prev) => ({ ...prev, costPreview: val }))}
          marks={{ 1: '$', 2: '$$', 3: '$$$', 4: '$$$$', 5: '$$$$$' }}
        />
      </Form.Item>
      <Form.Item label="Max Prompt Character Limit" help="Leave empty for no limit. Rejects the generation before it's queued if the prompt is longer.">
        <InputNumber
          min={1}
          style={{ width: 160 }}
          value={dialogState.maxCharacterLimit}
          onChange={(val) => setDialogState((prev) => ({ ...prev, maxCharacterLimit: val }))}
        />
      </Form.Item>
      <Divider />
      <Form.Item label="Has Audio Feature">
        <Switch
          checked={dialogState.hasAudioFeature}
          onChange={(val) => setDialogState((prev) => ({ ...prev, hasAudioFeature: val }))}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      {dialogState.hasAudioFeature && (
        <>
          <Form.Item label="Is Audio Optional">
            <Switch
              checked={dialogState.isAudioOptional}
              onChange={(val) => setDialogState((prev) => ({ ...prev, isAudioOptional: val }))}
              checkedChildren="Optional"
              unCheckedChildren="Always On"
            />
          </Form.Item>
          <Form.Item
            label="Audio Value Override"
            extra="Optional — only needed if this model's provider expects something other than true/false for audio (e.g. &quot;on&quot;/&quot;off&quot;). Leave blank to send the plain boolean."
          >
            <Space>
              <Input
                placeholder="On value (e.g. on)"
                style={{ width: 160 }}
                value={dialogState.audioOnValue}
                onChange={(e) => setDialogState((prev) => ({ ...prev, audioOnValue: e.target.value }))}
              />
              <Input
                placeholder="Off value (e.g. off)"
                style={{ width: 160 }}
                value={dialogState.audioOffValue}
                onChange={(e) => setDialogState((prev) => ({ ...prev, audioOffValue: e.target.value }))}
              />
            </Space>
          </Form.Item>
        </>
      )}
      <Form.Item label="Is Free Crop Image">
        <Switch
          checked={dialogState.isFreeCropImage}
          onChange={(val) => setDialogState((prev) => ({ ...prev, isFreeCropImage: val }))}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
        <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
          Whether image input uses free crop. The Crop Ratios list (below, on the model card) is UI-only and not sent to the backend as a generation parameter.
        </p>
      </Form.Item>

      <Divider />

      <Form.Item label="Resolutions & Pricing ($/sec)">
        <StandardChips
          standards={resolutionStandards}
          existingKeys={new Set((dialogState.resolutionRows || []).map((r) => r.key))}
          color="#52c41a"
          onAdd={(s) => setDialogState((prev) => ({
            ...prev,
            resolutionRows: [...(prev.resolutionRows || []), { key: s.key, value: s.key, name: s.label, priceOn: null, priceOff: null }],
          }))}
          onRemove={(key) => setDialogState((prev) => ({
            ...prev,
            resolutionRows: (prev.resolutionRows || []).filter((r) => r.key !== key),
          }))}
        />
        {(dialogState.resolutionRows || []).length > 0 && (
          <Table
            dataSource={dialogState.resolutionRows}
            rowKey={(_, i) => i}
            pagination={false}
            size="small"
            columns={[
              {
                title: 'Key',
                key: 'key',
                width: 110,
                render: (_, row, i) => row.key ? <Tag>{row.key}</Tag> : (
                  <LegacyKeyPicker
                    standards={resolutionStandards.filter((s) => !(dialogState.resolutionRows || []).some((r) => r.key === s.key))}
                    onPick={(key) => setDialogState((prev) => {
                      const s = resolutionStandards.find((x) => x.key === key);
                      if (!s) return prev;
                      const rows = JSON.parse(JSON.stringify(prev.resolutionRows));
                      rows[i] = { ...rows[i], key: s.key, name: s.label, value: rows[i].value || s.key };
                      return { ...prev, resolutionRows: rows };
                    })}
                  />
                ),
              },
              {
                title: 'Value',
                key: 'value',
                width: 140,
                render: (_, row, i) => (
                  <Input
                    size="small"
                    placeholder="e.g. 720p"
                    value={row.value}
                    onChange={(e) => setDialogState((prev) => {
                      const rows = JSON.parse(JSON.stringify(prev.resolutionRows));
                      rows[i].value = e.target.value;
                      return { ...prev, resolutionRows: rows };
                    })}
                  />
                ),
              },
              {
                title: 'Name',
                key: 'name',
                width: 140,
                render: (_, row) => <span style={{ color: '#666' }}>{row.name}</span>,
              },
              ...(dialogState.hasAudioFeature && dialogState.isAudioOptional ? [
                {
                  title: 'OFF ($/sec)',
                  key: 'priceOff',
                  width: 120,
                  render: (_, row, i) => (
                    <InputNumber
                      size="small" min={0} step={0.0001} style={{ width: '100%' }}
                      value={row.priceOff}
                      onChange={(val) => setDialogState((prev) => {
                        const rows = JSON.parse(JSON.stringify(prev.resolutionRows));
                        rows[i] = { ...rows[i], priceOff: val };
                        return { ...prev, resolutionRows: rows };
                      })}
                    />
                  ),
                },
                {
                  title: 'ON ($/sec)',
                  key: 'priceOn',
                  width: 120,
                  render: (_, row, i) => (
                    <InputNumber
                      size="small" min={0} step={0.0001} style={{ width: '100%' }}
                      value={row.priceOn}
                      onChange={(val) => setDialogState((prev) => {
                        const rows = JSON.parse(JSON.stringify(prev.resolutionRows));
                        rows[i] = { ...rows[i], priceOn: val };
                        return { ...prev, resolutionRows: rows };
                      })}
                    />
                  ),
                },
              ] : [
                {
                  title: dialogState.hasAudioFeature ? 'ON ($/sec)' : '$/sec',
                  key: 'price',
                  width: 120,
                  render: (_, row, i) => (
                    <InputNumber
                      size="small" min={0} step={0.0001} style={{ width: '100%' }}
                      value={row.priceOn}
                      onChange={(val) => setDialogState((prev) => {
                        const rows = JSON.parse(JSON.stringify(prev.resolutionRows));
                        rows[i] = { ...rows[i], priceOn: val };
                        return { ...prev, resolutionRows: rows };
                      })}
                    />
                  ),
                },
              ]),
              {
                title: 'Action',
                key: 'action',
                width: 60,
                render: (_, __, i) => (
                  <Button
                    danger size="small" type="text" icon={<DeleteOutlined />}
                    onClick={() => setDialogState((prev) => ({
                      ...prev,
                      resolutionRows: prev.resolutionRows.filter((_, idx) => idx !== i),
                    }))}
                  />
                ),
              },
            ]}
          />
        )}
        <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
          Durations for each resolution are added from the model card below, after saving. Existing durations are preserved when you edit a resolution's price here.
        </p>
      </Form.Item>

      <Form.Item label="Mark as Default Model">
        <Switch
          checked={dialogState.isDefault}
          onChange={(val) => setDialogState((prev) => ({ ...prev, isDefault: val }))}
          checkedChildren="Default"
          unCheckedChildren="Not Default"
        />
      </Form.Item>
    </Form>
  );

  // ---- Render ----

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Loading configuration...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1600px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Title level={2} style={{ color: 'black', margin: 0 }}>
          Unified Video Credit Configuration V2
        </Title>
        <Button
          icon={<SettingOutlined />}
          onClick={() => {
            setMinVersionInput(minVersion);
            setMinVersionModalVisible(true);
          }}
        >
          Set Minimum App Version{minVersion ? ` (${minVersion})` : ''}
        </Button>
      </div>

      <Modal
        title="Set Minimum App Version for Unified V2 Configs"
        open={minVersionModalVisible}
        onOk={handleSaveMinVersion}
        onCancel={() => setMinVersionModalVisible(false)}
        okText="Save"
        okButtonProps={{ disabled: minVersionInput === minVersion }}
        destroyOnClose
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Minimum App Version"
            extra="Users on this version or above will receive the unified V2 credit config and are expected to send raw ratio/resolution/duration values."
          >
            <Input
              value={minVersionInput}
              onChange={(e) => setMinVersionInput(e.target.value)}
              placeholder="e.g., 4.0.0"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Models card */}
      <Card
        title={<span style={{ fontSize: 18, fontWeight: 600 }}>Models</span>}
        extra={
          <Space>
            <Button icon={<SaveOutlined />} onClick={handleSave}>Save Changes</Button>
            <Button
              icon={<PlusOutlined />}
              onClick={() => setAddModelDialog({ ...EMPTY_MODEL_DIALOG, visible: true })}
            >
              Add Model
            </Button>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        {!unifiedConfig.models || unifiedConfig.models.length === 0 ? (
          <Empty description="No models configured yet" style={{ padding: '40px' }} />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleModelDragEnd}>
            <SortableContext items={unifiedConfig.models.map((m) => m.id)} strategy={verticalListSortingStrategy}>
              {unifiedConfig.models.map((model, modelIndex) => (
                <SortableModelWrapper key={model.id} id={model.id}>
                  <Collapse
                    style={{ flex: 1, border: 'none', borderRadius: 0 }}
                    expandIconPosition="end"
                    items={[{
                      key: '0',
                      label: (
                        <Space align="start">
                          {model.icon ? (
                            <img
                              src={model.icon}
                              alt={model.name}
                              style={{ width: 48, height: 48, borderRadius: 4, objectFit: 'cover', backgroundColor: '#f0f0f0', border: '1px solid #d9d9d9' }}
                            />
                          ) : (
                            <Avatar size={48} shape="square" style={{ marginTop: 4, backgroundColor: '#f0f0f0' }} icon={<SettingOutlined />} />
                          )}
                          <div style={{ flex: 1 }}>
                            <Space wrap>
                              <Badge status="processing" color="#52c41a" />
                              <strong>{model.name}</strong>
                              <Tag color="purple">{model.id}</Tag>
                              {model.badge && <Tag color="magenta">{model.badge}</Tag>}
                              {model.is_default && <Tag color="gold">Default Model</Tag>}
                              {model.has_audio_feature ? (
                                <Tag color="orange">
                                  Audio {model.is_audio_optional !== false ? '(Optional)' : '(Required)'}
                                </Tag>
                              ) : (
                                <Tag color="default">No Audio</Tag>
                              )}
                              {model.cost_preview && <Tag color="cyan">{'$'.repeat(model.cost_preview)}</Tag>}
                            </Space>
                            {model.description && (
                              <div style={{ marginTop: 8, fontSize: 13, fontWeight: 'normal', color: '#666' }}>
                                {model.description}
                              </div>
                            )}
                          </div>
                        </Space>
                      ),
                      extra: (
                        <Space>
                          <Tooltip title="Edit Model Settings">
                            <Button
                              type="default"
                              size="small"
                              icon={<SettingOutlined />}
                              onClick={() => {
                                setEditModelDialog({
                                  visible: true,
                                  modelIndex,
                                  modelId: model.id,
                                  modelName: model.name,
                                  description: model.description || '',
                                  badge: model.badge || '',
                                  icon: model.icon || '',
                                  iconFile: null,
                                  costPreview: model.cost_preview || 1,
                                  maxCharacterLimit: model.maxCharacterLimit ?? null,
                                  hasAudioFeature: model.has_audio_feature ?? false,
                                  isAudioOptional: model.is_audio_optional ?? true,
                                  audioOnValue: model.audioOnValue || '',
                                  audioOffValue: model.audioOffValue || '',
                                  isDefault: model.is_default === true,
                                  isFreeCropImage: model.is_free_crop_image ?? false,
                                  resolutionRows: resolutionsToRows(model.resolutions),
                                });
                              }}
                            >
                              Edit
                            </Button>
                          </Tooltip>
                          <Popconfirm
                            title="Remove Model"
                            description={`Remove ${model.name}?`}
                            onConfirm={() => handleRemoveModel(modelIndex)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button danger size="small" icon={<DeleteOutlined />}>Remove</Button>
                          </Popconfirm>
                        </Space>
                      ),
                      children: (
                        <>
                          <Collapse
                            style={{ marginBottom: 8 }}
                            items={[{
                              key: 'ratios',
                              label: <span style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1 }}>RATIOS</span>,
                              children: (
                                <>
                                  <RatioListEditor
                                    title="CROP RATIOS (UI-only, mobile input crop)"
                                    rows={model.cropRatios}
                                    onChange={(rows) => setRatioList(modelIndex, 'cropRatios', rows)}
                                    defaultValue={model.default_values?.cropAspectRatio}
                                    onSetDefault={(value) => setDefaultCropRatio(modelIndex, value)}
                                    withAspectXY
                                    standardsList={ratioStandards}
                                  />
                                  <RatioListEditor
                                    title="TEXT-TO-VIDEO OUTPUT RATIOS"
                                    rows={model.textToVideoRatios}
                                    onChange={(rows) => setRatioList(modelIndex, 'textToVideoRatios', rows)}
                                    defaultValue={model.default_values?.textToVideoAspectRatio}
                                    onSetDefault={(value) => setDefaultTextToVideoRatio(modelIndex, value)}
                                    standardsList={ratioStandards}
                                  />
                                  <RatioListEditor
                                    title="IMAGE-TO-VIDEO OUTPUT RATIOS"
                                    rows={model.imgToVideoRatios}
                                    onChange={(rows) => setRatioList(modelIndex, 'imgToVideoRatios', rows)}
                                    defaultValue={model.default_values?.imgToVideoAspectRatio}
                                    onSetDefault={(value) => setDefaultImgToVideoRatio(modelIndex, value)}
                                    standardsList={ratioStandards}
                                  />
                                </>
                              ),
                            }]}
                          />

                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1, marginBottom: 8 }}>DURATIONS</div>
                            <StandardChips
                              standards={durationStandards}
                              existingKeys={getGlobalDurationActiveSet(model)}
                              color="#fa8c16"
                              onAdd={(s) => toggleDurationGlobal(modelIndex, s, false)}
                              onRemove={(key) => toggleDurationGlobal(modelIndex, { key }, true)}
                            />
                          </div>

                          {model.has_audio_feature && model.is_audio_optional !== false && (
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1, marginBottom: 8 }}>DEFAULT AUDIO</div>
                              <Switch
                                checked={model.default_values?.isAudio === true}
                                onChange={(val) => setDefaultValue(modelIndex, 'isAudio', val)}
                                checkedChildren="On"
                                unCheckedChildren="Off"
                              />
                            </div>
                          )}

                          <div style={{ marginBottom: 4 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1 }}>RESOLUTIONS</div>
                          </div>

                          <Collapse
                            items={(model.resolutions || []).map((res, resIndex) => {
                              const hasAudio = model.has_audio_feature;
                              const isOptional = model.is_audio_optional !== false;
                              const pricing = res.pricing_per_second;
                              const isDefaultRes = model.default_values?.resolution != null && res.resolution?.key === model.default_values?.resolution;

                              return {
                                key: resIndex,
                                label: (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <Tooltip title={isDefaultRes ? 'Default' : 'Set as default'}>
                                      <Button
                                        type="text"
                                        size="small"
                                        icon={isDefaultRes ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                                        onClick={(e) => { e.stopPropagation(); setDefaultResolution(modelIndex, res.resolution?.key); }}
                                      />
                                    </Tooltip>
                                    {res.resolution?.key ? <Tag>{res.resolution.key}</Tag> : (
                                      <span onClick={(e) => e.stopPropagation()}>
                                        <LegacyKeyPicker
                                          standards={resolutionStandards}
                                          onPick={(key) => {
                                            const s = resolutionStandards.find((x) => x.key === key);
                                            if (s) assignLegacyResolutionKey(modelIndex, resIndex, s);
                                          }}
                                        />
                                      </span>
                                    )}
                                    {pricing !== undefined && pricing !== null && (
                                      hasAudio && typeof pricing === 'object' ? (
                                        <>
                                          {pricing.audio_off !== undefined && <Tag color="orange" style={{ fontSize: 11 }}>OFF: {pricing.audio_off} $/sec</Tag>}
                                          {pricing.audio_on !== undefined && <Tag color="purple" style={{ fontSize: 11 }}>ON: {pricing.audio_on} $/sec</Tag>}
                                        </>
                                      ) : typeof pricing === 'number' ? (
                                        <Tag color="purple" style={{ fontSize: 11 }}>{pricing} $/sec</Tag>
                                      ) : null
                                    )}
                                  </div>
                                ),
                                extra: (
                                  <Popconfirm
                                    title="Remove Resolution"
                                    description={`Remove ${res.resolution?.name}? This also removes its durations.`}
                                    onConfirm={() => handleRemoveResolution(modelIndex, resIndex)}
                                    okText="Yes"
                                    cancelText="No"
                                  >
                                    <Button danger size="small" icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()}>Remove</Button>
                                  </Popconfirm>
                                ),
                                children: (
                                  <Table
                                    columns={createDurationColumns(modelIndex, resIndex, hasAudio, isOptional, model.default_values?.duration)}
                                    dataSource={res.durations || []}
                                    rowKey={(record) => record.duration?.value || Math.random()}
                                    pagination={false}
                                    size="small"
                                    bordered
                                  />
                                ),
                              };
                            })}
                          />
                        </>
                      ),
                    }]}
                  />
                </SortableModelWrapper>
              ))}
            </SortableContext>
          </DndContext>
        )}
      </Card>

      {/* Add Model Modal */}
      <Modal
        title="Add Model"
        open={addModelDialog.visible}
        onOk={handleAddModel}
        onCancel={() => setAddModelDialog(EMPTY_MODEL_DIALOG)}
        width={700}
        destroyOnClose
      >
        {renderModelForm(addModelDialog, setAddModelDialog)}
      </Modal>

      {/* Edit Model Modal */}
      <Modal
        title="Edit Model"
        open={editModelDialog.visible}
        onOk={handleEditModel}
        onCancel={() => setEditModelDialog({ ...EMPTY_MODEL_DIALOG, modelIndex: -1 })}
        width={700}
        destroyOnClose
      >
        {renderModelForm(editModelDialog, setEditModelDialog)}
      </Modal>

    </div>
  );
};

export default UnifiedCreditConfigV2;
